import moment from 'moment/moment';
import {VAT} from '../../../../utils';

export const getCalculationData = (values, newPaycheck, currentMonth) => {
  const {
    commission,
    commissionValueType,
    creditCardCommission,
    creditCardCommissionValueType,
    compensations,
    compensationsValueType,
    employerEducationFund,
    employerEducationFundValueType,
    employerProvidentFund,
    employerProvidentFundValueType,
    employerMandatoryPension,
    employerMandatoryPensionValueType,
    employeeEducationFund,
    employeeEducationFundValueType,
    employeeProvidentFund,
    employeeProvidentFundValueType,
    employeeMandatoryPension,
    employeeMandatoryPensionValueType,
    expensesWithVat,
    expensesWithoutVat,
    exchangeFee,
    pensionManagementCommission,
    incomeTaxOffset,
    advancePayment,
    nationalInsurance,
    nationalInsuranceDebt,
    supplierExpensesRefund,
    year,
    month,
    splits,
    table,
    customLines,
    additionalCommission,
  } = values;

  const splitsValue = splits ? splits.value : 1;

  const getCostValue = (value) => {
    return value ? (0 - value) : 0;
  };

  const getAmountWithVat = () => {
    const amountFromTable = table
      .filter(item => item[`receipt-${item._id}`])
      .map(item => item.amount ? parseFloat(item.amount) : 0);
    const amountFromCustomLines = customLines
      .map(item => item.amount ? parseFloat(item.amount) : 0);
    return [...amountFromTable, ...amountFromCustomLines]
      .filter(Boolean)
      .map(parseFloat)
      .reduce((acc, value) => acc + value, 0);
  };

  const getVat = () => {
    const yearValue = year?.value ? year?.value : moment().year();
    const monthValue = month?.value ? month?.value : moment().month() + 1;
    const date = new Date(yearValue, monthValue, 1);
    const vatPercent = VAT.percents(date);
    const vatFromTable = table
      .filter(item => item[`receipt-${item._id}`])
      .filter(item => item[`receipt-${item._id}-vatIncluded`])
      .map(item => item.amount ? parseFloat(item.amount) * (1 - 100 / (100 + vatPercent)) : 0)
      .filter(Boolean);
    const vatFromCustomLines = customLines
      .filter(item => item.vatIncluded)
      .map(item => item.amount ? parseFloat(item.amount) * (1 - 100 / (100 + vatPercent)) : 0)
      .filter(Boolean);
    const vatFromExpenses = expensesWithVat
      ? expensesWithVat * (1 - 100 / (100 + vatPercent))
      : 0;
    const calculatedVat = [...vatFromTable, ...vatFromCustomLines]
      .reduce((acc, value) => acc + value, 0) - vatFromExpenses;
    const result = calculatedVat ? calculatedVat.toFixed(2) : 0;
    return result ? parseFloat(result) : 0;
  };

  const vatValue = getVat();

  const getAmountPercent = (base, valueType, value) => {
    if (valueType.includes('amount')) {
      return value ? parseFloat(value).toFixed(2) : 0;
    } else {
      return (base && value)
        ? (base * parseFloat(value) / 100).toFixed(2)
        : 0;
    }
  };

  const additionalCommissionValue = additionalCommission ? parseFloat(additionalCommission) : 0;

  const getPensionManagementCommission = () => {
    if (newPaycheck) {
      const hasPension = [
        getAmountPercent(getAmountWithVat(), compensationsValueType, compensations),
        getAmountPercent(getAmountWithVat(), employerEducationFundValueType, employerEducationFund),
        getAmountPercent(getAmountWithVat(), employerMandatoryPensionValueType, employerMandatoryPension),
        getAmountPercent(getAmountWithVat(), employerProvidentFundValueType, employerProvidentFund),
        getAmountPercent(getAmountWithVat(), employeeEducationFundValueType, employeeEducationFund),
        getAmountPercent(getAmountWithVat(), employeeMandatoryPensionValueType, employeeMandatoryPension),
        getAmountPercent(getAmountWithVat(), employeeProvidentFundValueType, employeeProvidentFund),
      ].filter(Boolean).map(parseFloat).reduce((acc, value) => acc + value, 0);

      return hasPension ? pensionManagementCommission : 0;
    }
  };

  const getEmployerCost = () => {
    const costs = [
      expensesWithVat,
      vatValue,
      expensesWithoutVat,
      exchangeFee,
      getAmountPercent(getAmountWithVat(), creditCardCommissionValueType, creditCardCommission),
      getAmountPercent(getAmountWithVat(), commissionValueType, commission),
      additionalCommissionValue,
      getPensionManagementCommission()
    ].filter(Boolean).map(parseFloat).reduce((acc, value) => acc + value, 0);

    return getAmountWithVat() - costs;
  };

  const getBrutto = () => {
    const costs = [
      getAmountPercent(getEmployerCost(), compensationsValueType, compensations),
      getAmountPercent(getEmployerCost(), employerEducationFundValueType, employerEducationFund),
      getAmountPercent(getEmployerCost(), employerMandatoryPensionValueType, employerMandatoryPension),
      getAmountPercent(getEmployerCost(), employerProvidentFundValueType, employerProvidentFund),
    ].filter(Boolean).map(parseFloat).reduce((acc, value) => acc + value, 0);

    return getEmployerCost() - costs;
  };

  const getSplitBrutto = () => getBrutto() / splitsValue;

  const getNetto = () => {
    const costs = [
      incomeTaxOffset,
      getAmountPercent(getSplitBrutto(), employeeEducationFundValueType, employeeEducationFund),
      getAmountPercent(getSplitBrutto(), employeeMandatoryPensionValueType, employeeMandatoryPension),
      getAmountPercent(getSplitBrutto(), employeeProvidentFundValueType, employeeProvidentFund),
    ].filter(Boolean).map(parseFloat).reduce((acc, value) => acc + value, 0);

    return getSplitBrutto() - costs;
  };

  const supplierExpensesRefundValue = supplierExpensesRefund
    ? parseFloat(supplierExpensesRefund) : 0;

  const getExpensesRefund = () => {
    const expenses = [
      expensesWithVat,
      expensesWithoutVat
    ].filter(Boolean).map(parseFloat).reduce((acc, value) => acc + value, 0);

    return expenses - supplierExpensesRefundValue;
  };

  const getNationalInsurance = () => newPaycheck ? nationalInsurance : 0;

  const getToBePaid = () => {
    const costs = [
      advancePayment,
      getNationalInsurance(),
      nationalInsuranceDebt
    ].filter(Boolean).map(parseFloat).reduce((acc, value) => acc + value, 0);

    const result = getNetto() + getExpensesRefund() - costs;

    return Boolean(result) ? Math.round(result) : 0;
  };

  const currentMonthBrutto = newPaycheck ? currentMonth.brutto : currentMonth.brutto - getBrutto();

  return {
    amountWithVat: getAmountWithVat(),
    expensesWithVat: getCostValue(expensesWithVat),
    vat: getCostValue(vatValue),
    vatValue: getCostValue(vatValue),
    amountWithoutVat: getAmountWithVat() - Math.abs(vatValue),
    expensesWithoutVat: getCostValue(expensesWithoutVat),
    exchangeFee: getCostValue(exchangeFee),
    pensionManagementCommission: getCostValue(getPensionManagementCommission()),
    employerCost: getEmployerCost(),

    commission: getCostValue(getAmountPercent(getAmountWithVat(), commissionValueType, commission)),
    creditCardCommission: getCostValue(getAmountPercent(getAmountWithVat(), creditCardCommissionValueType, creditCardCommission)),
    compensations: getCostValue(getAmountPercent(getEmployerCost(), compensationsValueType, compensations)),
    employerEducationFund: getCostValue(getAmountPercent(getEmployerCost(), employerEducationFundValueType, employerEducationFund)),
    employerMandatoryPension: getCostValue(getAmountPercent(getEmployerCost(), employerMandatoryPensionValueType, employerMandatoryPension)),
    employerProvidentFund: getCostValue(getAmountPercent(getEmployerCost(), employerProvidentFundValueType, employerProvidentFund)),
    employeeEducationFund: getCostValue(getAmountPercent(getSplitBrutto(), employeeEducationFundValueType, employeeEducationFund)),
    employeeMandatoryPension: getCostValue(getAmountPercent(getSplitBrutto(), employeeMandatoryPensionValueType, employeeMandatoryPension)),
    employeeProvidentFund: getCostValue(getAmountPercent(getSplitBrutto(), employeeProvidentFundValueType, employeeProvidentFund)),

    brutto: getBrutto(),
    accumulatedBrutto: getBrutto() + currentMonthBrutto,
    splitBrutto: getSplitBrutto(),
    incomeTaxOffset: incomeTaxOffset ? parseFloat(incomeTaxOffset) : 0,
    netto: getNetto(),
    advancePayment: getCostValue(advancePayment),
    nationalInsurance: getCostValue(getNationalInsurance()),
    nationalInsuranceDebt: getCostValue(nationalInsuranceDebt),
    supplierExpensesRefund: supplierExpensesRefundValue,
    expensesRefund: getExpensesRefund(),
    toBePaid: getToBePaid(),
    splits: splits.value,
    additionalCommission: getCostValue(additionalCommissionValue),
    year
  };
};